import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { solarPlanets } from "../data/solarPlanets";
import SolarPlanetRenderer from "../components/SolarPlanetRenderer";
import "./SolarLabPage.css";

export default function SolarLabPage() {
  const [activePlanet, setActivePlanet] = useState(null);
  const [filterCategory, setFilterCategory] = useState("All");
  const detailRef = useRef(null);
  const gridRef = useRef(null);

  // Gather unique categories
  const categories = [
    "All",
    ...Array.from(new Set(solarPlanets.map((p) => p.category))),
  ];

  const filtered =
    filterCategory === "All"
      ? solarPlanets
      : solarPlanets.filter((p) => p.category === filterCategory);

  // Close detail on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActivePlanet(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Scroll to detail when opening
  useEffect(() => {
    if (activePlanet && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activePlanet]);

  return (
    <div className="solar-lab">
      {/* Header */}
      <header className="solar-lab__header">
        <Link to="/" className="solar-lab__logo">
          DYLAN CHEN ZHEN
        </Link>
        <nav className="solar-lab__nav">
          <Link to="/projects" className="solar-lab__nav-link">
            Projects
          </Link>
          <Link to="/lab" className="solar-lab__nav-link">
            Lab
          </Link>
          <Link to="/about" className="solar-lab__nav-link">
            About
          </Link>
        </nav>
      </header>

      {/* Hero intro */}
      <section className="solar-lab__hero">
        <span className="solar-lab__eyebrow">Solar Energy Research</span>
        <h1 className="solar-lab__title">Solar Worlds</h1>
        <p className="solar-lab__subtitle">
          20 procedural 3D planets exploring every facet of solar energy — from
          photovoltaic cells and solar cars to AI grid optimization and deep
          learning forecasting. Each planet is a unique WebGL shader.
        </p>
      </section>

      {/* Category filter */}
      <div className="solar-lab__filters">
        {categories.map((cat) => (
          <button
            key={cat}
            className={`solar-lab__filter-btn${
              filterCategory === cat ? " is-active" : ""
            }`}
            onClick={() => {
              setFilterCategory(cat);
              setActivePlanet(null);
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Planet grid */}
      <div className="solar-lab__grid" ref={gridRef}>
        {filtered.map((planet) => (
          <button
            key={planet.id}
            className={`solar-lab__card${
              activePlanet?.id === planet.id ? " is-active" : ""
            }`}
            onClick={() =>
              setActivePlanet(activePlanet?.id === planet.id ? null : planet)
            }
          >
            <div className="solar-lab__card-planet">
              <SolarPlanetRenderer
                shader={planet.shader}
                color={planet.color}
                accent={planet.accent}
                size={220}
                autoRotate
                interactive={false}
              />
            </div>
            <div className="solar-lab__card-info">
              <span className="solar-lab__card-category">
                {planet.category}
              </span>
              <h3 className="solar-lab__card-name">{planet.name}</h3>
              <p className="solar-lab__card-subtitle">{planet.subtitle}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Detail panel */}
      {activePlanet && (
        <div className="solar-lab__detail" ref={detailRef}>
          <div className="solar-lab__detail-planet">
            <SolarPlanetRenderer
              shader={activePlanet.shader}
              color={activePlanet.color}
              accent={activePlanet.accent}
              size={400}
              autoRotate
              interactive
            />
          </div>
          <div className="solar-lab__detail-content">
            <span className="solar-lab__detail-category">
              {activePlanet.category}
            </span>
            <h2 className="solar-lab__detail-name">{activePlanet.name}</h2>
            <p className="solar-lab__detail-subtitle">
              {activePlanet.subtitle}
            </p>
            <div className="solar-lab__detail-divider" />
            <p className="solar-lab__detail-description">
              {activePlanet.description}
            </p>
            <div className="solar-lab__detail-meta">
              <div className="solar-lab__detail-meta-item">
                <span className="solar-lab__detail-meta-label">Shader</span>
                <span className="solar-lab__detail-meta-value">
                  {activePlanet.shader}
                </span>
              </div>
              <div className="solar-lab__detail-meta-item">
                <span className="solar-lab__detail-meta-label">Rendering</span>
                <span className="solar-lab__detail-meta-value">
                  Three.js + Custom GLSL
                </span>
              </div>
              <div className="solar-lab__detail-meta-item">
                <span className="solar-lab__detail-meta-label">Geometry</span>
                <span className="solar-lab__detail-meta-value">
                  128-segment UV Sphere
                </span>
              </div>
            </div>
            <button
              className="solar-lab__detail-close"
              onClick={() => setActivePlanet(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Footer count */}
      <footer className="solar-lab__footer">
        <p>
          {filtered.length} of {solarPlanets.length} planets
          {filterCategory !== "All" ? ` in ${filterCategory}` : ""}
        </p>
      </footer>
    </div>
  );
}
